import React from 'react';
import { Link } from 'react-router-dom';
import { colors, semanticColors } from '../../theme';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useScrollToTop } from '../../hooks/useScrollToTop';

const PrivacyPolicy: React.FC = () => {
  // Scroll to top when component mounts
  useScrollToTop('smooth');

  return (
    <div className="min-h-screen" style={{ backgroundColor: semanticColors.background.primary }}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center text-sm hover:underline transition-colors duration-200 mb-4"
            style={{ color: colors.primary }}
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: colors.primary }}
          >
            Vital Hive Technologies
          </h1>
          <p className="text-lg font-medium mb-1" style={{ color: colors.primary }}>
            Privacy Policy
          </p>
          <p className="text-sm" style={{ color: semanticColors.text.secondary }}>
            Last Updated: August 06, 2025
          </p>
        </div>

        {/* Preamble */}
        <div className="mb-8 p-6 rounded-lg" style={{ backgroundColor: semanticColors.background.secondary, border: `1px solid ${semanticColors.border.light}` }}>
          <p className="text-sm leading-relaxed mb-4" style={{ color: semanticColors.text.secondary }}>
            Vital Hive Technologies LLC and its affiliates ("Vital Hive," "VHT," "Vital Hive Clinician App," "we," "us," or "our") respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy ("Policy") explains how Vital Hive collects, uses, discloses, and safeguards your information when you visit our website (vitalhivetechnologies.comwww.bluewaterhealthhcm.com), VHT Facility Portal, VHT Admin Portal, mobile application (VHT Clinician), or use our services (collectively, the "Services").
          </p>
          <p className="text-sm leading-relaxed mb-4" style={{ color: semanticColors.text.secondary }}>
            This Privacy Policy applies to:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm" style={{ color: semanticColors.text.secondary }}>
            <li>Our website;</li>
            <li>VHT Facility Portal</li>
            <li>VHT Admin Portal</li>
            <li>VHT mobile application (VHT Clinician)</li>
            <li>All related services, technology, features, and content available through the above (collectively, the "Platform").</li>
          </ul>
          <p className="text-sm leading-relaxed mt-4" style={{ color: semanticColors.text.secondary }}>
            By accessing or using the Platform, you agree to this Privacy Policy. If you do not agree, please do not access or use the Services.
          </p>
        </div>

        {/* Section 1: Information We Collect */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            1. Information We Collect
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              1.1 Information You Provide
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              We collect personal information you voluntarily provide when you:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Register or create an account;</li>
              <li>Complete credentialing or scheduling forms;</li>
              <li>Communicate with Facilities or our support team;</li>
              <li>Apply for shifts or respond to messages.</li>
            </ul>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              This may include:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Name, contact details (email, phone, address), username/password;</li>
              <li>Date of birth, gender, and demographic details;</li>
              <li>Professional credentials, licenses, certifications, SSN, and government-issued IDs</li>
              <li>Employment eligibility documentation;</li>
              <li>Profile photos and resumes;</li>
              <li>Bank account or payment details;</li>
              <li>Communications, inquiries, and support tickets;</li>
              <li>Any content you upload or submit.</li>
            </ul>
            <p className="text-sm italic" style={{ color: semanticColors.text.tertiary }}>
              Note: Any documents you upload (e.g., licenses, forms) are not redacted by Vital Hive. Please redact sensitive information prior to uploading if desired.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              1.2 For Providers
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              If you are a healthcare Provider, we may collect:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Background check authorizations;</li>
              <li>Work availability and scheduling preferences;</li>
              <li>Calendar access (if enabled);</li>
              <li>Location data (for time-tracking, invoicing, and shift verification);</li>
              <li>Messages to and from Facilities;</li>
              <li>Ratings, reviews, or performance feedback.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              1.3 For Facilities
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              If you are a Facility user:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Name, job title, and contact information;</li>
              <li>Facility name, type, and licensing details;</li>
              <li>Shift posting details;</li>
              <li>Messages with Providers;</li>
              <li>Scheduling and billing records;</li>
              <li>Survey or support responses.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              1.4 Automatically Collected Information
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              We use cookies and other technologies to collect:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Device type, OS, and browser;</li>
              <li>IP address and general geolocation;</li>
              <li>Usage metrics (pages viewed, session times, link clicks);</li>
              <li>Referring/exit URLs;</li>
              <li>Error logs and language preferences.</li>
            </ul>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              With your consent (via device settings), we may collect precise location data for shift check-in/out and digital invoicing.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              1.5 Cookies and Analytics
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              We use cookies and similar technologies to understand user behavior, save preferences and settings, improve site performance, and support marketing efforts (where permitted).
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Authenticate users;</li>
              <li>Maintain session preferences;</li>
              <li>Track usage for security and platform improvement;</li>
              <li>Deliver targeted advertising (subject to your choices).</li>
            </ul>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              We use third-party analytics (e.g., Google Analytics).
            </p>
          </div>
        </section>

        {/* Section 2: How We Use Your Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            2. How We Use Your Information
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            We use your information to:
          </p>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              2.1 Operate the Platform
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Create and manage user accounts;</li>
              <li>Match Providers with Facility shifts;</li>
              <li>Communicate shift requests, confirmations, or changes;</li>
              <li>Enable invoicing, payment, and support functions;</li>
              <li>Display Provider profiles and Facility details;</li>
              <li>Respond to inquiries and provide customer support.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              2.2 Maintain Trust and Safety
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Verify user identity, licensing, and credentials;</li>
              <li>Conduct background checks (where legally permissible);</li>
              <li>Prevent fraud, abuse, and prohibited conduct;</li>
              <li>Monitor and enforce compliance with our Terms of Service.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              2.3 Improve and Promote Our Services
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Understand usage trends and develop new features;</li>
              <li>Personalize your experience;</li>
              <li>Send surveys and collect feedback;</li>
              <li>Send promotional or service-related communications.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              2.4 Comply with Legal Obligations
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Respond to law enforcement or regulatory requests;</li>
              <li>Enforce our contracts and rights;</li>
              <li>Maintain audit, tax, or legal records.</li>
            </ul>
          </div>
        </section>

        {/* Section 3: How We Share Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            3. How We Share Information
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            We may share your information with:
          </p>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.1 With Consent or At Your Direction
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Sharing Provider profiles and credentials with Facilities;</li>
              <li>Enabling Facilities to verify shift attendance via location tracking.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.2 Service Providers
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Credentialing and background check vendors;</li>
              <li>Cloud hosting, payment processors, and customer support platforms;</li>
              <li>Communication providers and analytics vendors.</li>
            </ul>
            <p className="mt-3" style={{ color: semanticColors.text.secondary }}>
              All vendors are bound by confidentiality and data protection obligations.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.3 Third-Party Advertising
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Certain non-sensitive data may be shared for targeted ads. You can opt out via "Your Privacy Choices" in the website site footer or in the VHT Clinician app.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.4 Legal and Regulatory Compliance
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>To comply with subpoenas, court orders, and law enforcement;</li>
              <li>To protect Vital Hive, users, or the public from harm or fraud.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.5 Business Transfers
            </h3>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>In the event of a merger, acquisition, asset sale, bankruptcy, or reorganization, your information may be transferred.</li>
            </ul>
          </div>
        </section>

        {/* Section 4: Data Security */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            4. Data Security
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            We implement technical and organizational safeguards to protect your data. These include encryption, access controls, and audit logging. However, no system is completely secure. If you believe your interaction with us is no longer secure, please contact us immediately.
          </p>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            You are responsible for maintaining the confidentiality of your account credentials.
          </p>
        </section>

        {/* Section 5: Your Rights and Choices */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            5. Your Rights and Choices
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.1 Managing Your Account
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You can update, correct, or delete profile information through your dashboard. To request account deletion, contact us at support@vitalhivetechnologies.com.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.2 Marketing Preferences
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You can opt out of promotional emails by using the "unsubscribe" link or by contacting us at support@vitalhivetechnologies.com. You may still receive essential service notifications.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.3 Privacy Rights by Jurisdiction
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Depending on your location, you may have rights under applicable law, such as:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li><strong>Access:</strong> Requesting a copy of the personal information we hold about you.</li>
              <li><strong>Rectification:</strong> Requesting the correction of any inaccurate or incomplete personal information.</li>
              <li><strong>Erasure (Right to be Forgotten):</strong> Requesting the deletion of your personal information under certain circumstances.</li>
              <li><strong>Objection:</strong> Objecting to the processing of your personal information in certain situations.</li>
              <li><strong>Portability:</strong> Requesting that we provide you with your personal information in a structured, commonly used, and machine-readable format.</li>
            </ul>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Submit requests to support@vitalhivetechnologies.com. We will verify your identity before fulfilling them. Some information may be exempt from deletion under law (e.g., transaction history, legal obligations).
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.4 Do Not Sell or Share
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You may opt out of the "sale" or "sharing" of personal information for targeted advertising purposes by visiting Your Privacy Choices on our website.
            </p>
          </div>
        </section>

        {/* Section 6: Data Retention */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            6. Data Retention
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            We retain your information:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2" style={{ color: semanticColors.text.secondary }}>
            <li>As long as necessary to provide the Services;</li>
            <li>To comply with legal obligations;</li>
            <li>For audit, fraud prevention, and dispute resolution.</li>
          </ul>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            Inactive or deleted accounts may retain archival data in backup systems for a limited time.
          </p>
        </section>

        {/* Section 7: Third-Party Links */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            7. Third-Party Links
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            Our Platform may link to third-party websites or services. This Privacy Policy does not apply to those external services. Please review their privacy practices independently.
          </p>
        </section>

        {/* Section 8: Children's Privacy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            8. Children's Privacy
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            The Platform is not intended for children under 13. We do not knowingly collect personal information from children. If you believe we have done so, please contact us for deletion.
          </p>
        </section>

        {/* Section 9: International Users */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            9. International Users
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            Our Platform is hosted in the United States. By using our Services, you acknowledge that your personal information will be transferred to, stored in, and processed in the U.S., where data protections may differ from your home country.
          </p>
        </section>

        {/* Section 10: Changes to this Privacy Policy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            10. Changes to this Privacy Policy
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            We may update this Privacy Policy periodically. If we make material changes, we will notify you by updating the "Last Updated" date and providing notice via the Platform or email. Your continued use of the Services indicates acceptance of the changes.
          </p>
        </section>

        {/* Section 11: Contact Us */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            11. Contact Us
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            For questions, concerns, or privacy requests:
          </p>
          <div className="space-y-2" style={{ color: semanticColors.text.secondary }}>
            <p><strong>Vital Hive Technologies LLC</strong></p>
            <p><strong>Email:</strong> support@vitalhivetechnologies.com</p>
            <p><strong>Address:</strong> 3950 Sunforest Ct. Suite 200 Toledo, Ohio 43623</p>
          </div>
        </section>

        {/* Section 12: Reminder */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            12. Reminder
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            By using our Services, you acknowledge that you have read and understood this Privacy Policy and consent to our data practices as described herein. For more information about the rules and guidelines governing the use of our website and services, please consult our{' '}
            <Link 
              to="/terms-of-service"
              className="hover:underline transition-colors duration-200"
              style={{ color: colors.primary }}
            >
              Terms of Service
            </Link>.
          </p>
        </section>

        {/* Contact */}
        <div className="mt-12 p-6 rounded-lg text-center" style={{ backgroundColor: semanticColors.background.secondary, border: `1px solid ${semanticColors.border.light}` }}>
          <p className="mb-2" style={{ color: semanticColors.text.secondary }}>
            Questions about this Privacy Policy?
          </p>
          <a 
            href="mailto:support@vitalhivetechnologies.com"
            className="text-sm hover:underline transition-colors duration-200"
            style={{ color: colors.primary }}
          >
           support@vitalhivetechnologies.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
